/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import { DataTableColumn, RowSelection } from 'types/table.types';
import { flexRender } from '@tanstack/react-table';

export interface TableBodyProps<T> {
  headers: Array<{
    id: string;
    label: string;
    enableSorting: boolean;
    onSort: () => void;
  }>;
  rows: Array<{
    id: string;
    data: T;
    isSelected: boolean;
    onSelect: (id: string) => void;
  }>;
  enableRowSelection?: boolean;
  enableHover?: boolean;
  striped?: boolean;
  bordered?: boolean;
  dark?: boolean;
  onRowClick?: (row: T) => void;
  rowRenderer?: (row: T) => React.ReactNode;
  emptyMessage?: string;
}

export const TableBody = React.memo(
  React.forwardRef<HTMLDivElement, TableBodyProps<any>>(
    (
      {
        headers,
        rows,
        enableRowSelection = false,
        enableHover = true,
        striped = true,
        bordered = true,
        dark = false,
        onRowClick,
        emptyMessage = 'No data available',
      },
      ref,
    ) => {
      const handleRowClick = useCallback(
        (rowData: any, e: React.MouseEvent) => {
          // Don't trigger if clicking on checkbox
          if ((e.target as HTMLElement).tagName === 'INPUT') {
            return;
          }
          onRowClick?.(rowData);
        },
        [onRowClick],
      );

      if (rows.length === 0) {
        return (
          <div ref={ref} className="text-center py-5">
            <i className="ri-inbox-2-line" style={{ fontSize: '3rem', color: '#ccc' }}></i>
            <p className="text-muted mt-2">{emptyMessage}</p>
          </div>
        );
      }

      return (
        <div ref={ref}>
          <table
            className={`table ${striped ? 'table-striped' : ''} ${
              bordered ? 'table-bordered' : ''
            } ${enableHover ? 'table-hover' : ''} ${
              dark ? 'table-dark' : ''
            } table-nowrap align-middle mb-0`.trim()}
          >
            <thead className="table-light">
              <tr>
                {enableRowSelection && (
                  <th style={{ width: '50px' }}>
                    <div className="d-flex align-items-center">
                      <input type="checkbox" id="select-all" className="form-check-input" />
                    </div>
                  </th>
                )}
                {headers.map((header) => (
                  <th
                    key={header.id}
                    className={header.enableSorting ? 'cursor-pointer' : ''}
                    onClick={header.enableSorting ? header.onSort : undefined}
                    style={{ userSelect: 'none' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span>{header.label}</span>
                      {header.enableSorting && (
                        <i className="ri-arrow-up-down-line small text-muted"></i>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={(e) => handleRowClick(row.data, e)}
                  className={row.isSelected ? 'table-active' : ''}
                  style={{ cursor: onRowClick ? 'pointer' : 'auto' }}
                >
                  {enableRowSelection && (
                    <td>
                      <input
                        type="checkbox"
                        id={`row-${row.id}`}
                        checked={row.isSelected}
                        onChange={() => row.onSelect(row.id)}
                        className="form-check-input"
                      />
                    </td>
                  )}
                  {headers.map((header) => (
                    <td key={`${row.id}-${header.id}`}>{flexRender(header.label, {})}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
  ),
);

TableBody.displayName = 'TableBody';
